import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CustomerTable = ({ customers, onEdit, onDelete }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const getRiskColor = (percentage) => {
    if (percentage >= 30) return 'text-error';
    if (percentage >= 15) return 'text-warning';
    return 'text-success';
  };

  const getRiskBadge = (percentage) => {
    if (percentage >= 30) return { label: 'Tinggi', color: 'bg-error/10 text-error' };
    if (percentage >= 15) return { label: 'Sedang', color: 'bg-warning/10 text-warning' };
    return { label: 'Rendah', color: 'bg-success/10 text-success' };
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedCustomers = [...customers]?.sort((a, b) => {
    if (!sortConfig?.key) return 0;
    
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];
    
    if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="w-full">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">
                <button 
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  Nama Pelanggan
                  <Icon name="ArrowUpDown" size={16} />
                </button>
              </th>
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">
                <button 
                  onClick={() => handleSort('volume')}
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  Volume (L/bulan)
                  <Icon name="ArrowUpDown" size={16} />
                </button>
              </th>
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">Syarat Pembayaran</th>
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">Durasi Kontrak</th>
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">
                <button 
                  onClick={() => handleSort('dependencyPercentage')}
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  Ketergantungan
                  <Icon name="ArrowUpDown" size={16} />
                </button>
              </th>
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">Risiko</th>
              <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sortedCustomers?.map((customer) => {
              const risk = getRiskBadge(customer?.dependencyPercentage);
              return (
                <tr key={customer?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Building2" size={20} color="var(--color-primary)" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{customer?.name}</p>
                        <p className="text-sm text-muted-foreground">{customer?.type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="font-medium text-foreground data-text">
                      {customer?.volume?.toLocaleString('id-ID')}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-foreground">{customer?.paymentTerms}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-foreground">{customer?.contractDuration}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`font-semibold data-text ${getRiskColor(customer?.dependencyPercentage)}`}>
                      {customer?.dependencyPercentage}%
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${risk?.color}`}>
                      {risk?.label}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(customer)}
                        iconName="Edit2"
                        iconSize={16}
                      >
                        <span className="sr-only">Edit</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(customer?.id)}
                        iconName="Trash2"
                        iconSize={16}
                      >
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {sortedCustomers?.map((customer) => {
          const risk = getRiskBadge(customer?.dependencyPercentage);
          return (
            <div key={customer?.id} className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Building2" size={24} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{customer?.name}</p>
                    <p className="text-sm text-muted-foreground">{customer?.type}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${risk?.color}`}>
                  {risk?.label}
                </span>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Volume Bulanan</span>
                  <span className="font-medium text-foreground data-text">
                    {customer?.volume?.toLocaleString('id-ID')} L
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Pembayaran</span>
                  <span className="text-sm text-foreground">{customer?.paymentTerms}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Kontrak</span>
                  <span className="text-sm text-foreground">{customer?.contractDuration}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Ketergantungan</span>
                  <span className={`font-semibold data-text ${getRiskColor(customer?.dependencyPercentage)}`}>
                    {customer?.dependencyPercentage}%
                  </span>
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  onClick={() => onEdit(customer)}
                  iconName="Edit2"
                  iconPosition="left"
                  iconSize={16}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  onClick={() => onDelete(customer?.id)}
                  iconName="Trash2"
                  iconPosition="left"
                  iconSize={16}
                >
                  Hapus
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomerTable;